/* ==== Batch page is cached on cdn for 1 hours ==== */
import Layout from '@layout/index';
import Batch from '@cont/Batch/Batch';
import {
  fetchBatchData,
  fetchTopicsCovered,
  fetchTestSeries,
  fetchSchedule,
  fetchTrialClasses,
  fetchBatchFaq,
  fetchBatchFeedbacks,
  fetchBatchDemoClasses,
  fetchStorePlaylistFeed,
  fetchStorePlaylistTestFeed
} from '@api/fetchers/batch';
import { BATCH_PAGE } from '@constants/segment';
import { useSelector } from 'react-redux';
import { getMe } from '@selectors/auth';
// SWR and API functions
import { SWRConfig } from 'swr';
import { getKey as getBatchDetailsKey } from '@api/hooks/batch/useBatchDetails';
import useBatchSchedule from '@api/hooks/batch/useBatchSchedule';
import { getKey as getBatchTopickey } from '@api/hooks/batch/useBatchTopicsCovered';
import { getKey as getBatchTestSeriesKey } from '@api/hooks/batch/useBatchTestSeries';
import { getKey as getBatchTrailkey } from '@api/hooks/batch/useBatchTrialClasses';
import { getKey as getBatchFeedback } from '@api/hooks/batch/useBatchFeedback';
import { getKey as getStorePlaylistTestFeed } from '@api/hooks/batch/useStorePlaylistTestFeed';
import cookies, { readCookie } from '@utils/cookies';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { getBatchDemoClassesKey } from '@api/hooks/batch/useDemoClasses';
import { getKey as getStorePlaylistFeed } from '@api/hooks/batch/useStorePlaylistFeed';
import {
  FETCH_STORE_SYLLABUS_VALUES,
  SEVEN_DAY_FREE_TRIAL
} from '@cont/Batch/utils';
import { fetchFreeTrialInfo } from '@cont/NewPreSubscribed/FreeTrial/api';
import { useEffect } from 'react';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useFollowGoal from '@api/hooks/useFollowGoal';

const BatchPage = ({ goalUID, batchUID, fallbackData, freeTrialData }) => {
  const me = useSelector(getMe);
  const { faqs = [] } = fallbackData || {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { followCurrentGoal } = useFollowGoal(goalUID);
  useEffect(() => {
    if (isLoggedIn) {
      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  useEffect(() => {
    validateAndSetSessionId();
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    fetchABExperimentData(SEVEN_DAY_FREE_TRIAL, abProxyJWT, deviceID);
  }, []);
  return (
    <Batch
      batchUID={batchUID}
      me={me}
      faqs={faqs}
      freeTrialData={freeTrialData}
    />
  );
};

const BatchPageWithSWR = ({
  goalUID,
  batchUID,
  freeTrialData,
  fallbackData
}) => {
  const batchDetailsKey = getBatchDetailsKey(batchUID);
  const { key: batchScheduleKey } = useBatchSchedule(batchUID);
  const batchTopicsCoveredKey = getBatchTopickey(batchUID);
  const batchTestSeriesKey = getBatchTestSeriesKey(batchUID);
  const batchTrialClassesKey = getBatchTrailkey(batchUID);
  const batchfeedbackKey = getBatchFeedback(goalUID);
  const batchDemoClassKey = getBatchDemoClassesKey(batchUID);
  const storePlaylistFeed = getStorePlaylistFeed(batchUID);
  const storePlaylistTestFeed = getStorePlaylistTestFeed({ batchUID });
  const accToken = readCookie('accessToken');
  return (
    <SWRConfig
      value={{
        revalidateOnMount: !!accToken,
        dedupingInterval: 60 * 1000,
        fallback: {
          [batchDetailsKey]: fallbackData?.batchDetails,
          [batchScheduleKey]: fallbackData?.schedule,
          [batchTopicsCoveredKey]: fallbackData?.topicsCovered,
          [batchTestSeriesKey]: fallbackData?.testSeries,
          [batchTrialClassesKey]: fallbackData?.trialClasses,
          [batchfeedbackKey]: fallbackData?.feedback,
          [batchDemoClassKey]: fallbackData?.demoClasses,
          [storePlaylistFeed]: fallbackData?.storePlaylistFeed,
          [storePlaylistTestFeed]: fallbackData?.storePlaylistTestFeed
        }
      }}
    >
      <BatchPage
        goalUID={goalUID}
        batchUID={batchUID}
        fallbackData={fallbackData}
        freeTrialData={freeTrialData}
      />
      <OneLinkSmartScript />
    </SWRConfig>
  );
};

BatchPageWithSWR.pageInfo = {
  name: BATCH_PAGE,
  isCMSMeta: true,
  isMWebSupported: true
};

export default BatchPageWithSWR;

export const getStaticProps = async ({ params }) => {
  try {
    const { batchUID } = params;
    const promises = [];
    promises.push(fetchBatchData(batchUID));
    promises.push(fetchTopicsCovered(batchUID));
    promises.push(fetchTestSeries(batchUID));
    promises.push(fetchSchedule(batchUID));
    promises.push(fetchTrialClasses(batchUID));
    promises.push(fetchBatchFaq());
    promises.push(fetchBatchDemoClasses(batchUID));
    promises.push(fetchStorePlaylistFeed(batchUID));
    promises.push(
      fetchStorePlaylistTestFeed({
        batchUID,
        offset: FETCH_STORE_SYLLABUS_VALUES.OFFSET
      })
    );

    const [
      batchDetails,
      topicsCovered,
      testSeries,
      schedule,
      trialClasses,
      faqsResponse,
      demoClasses,
      storePlaylistFeed,
      storePlaylistTestFeed
    ] = await Promise.all(promises);
    // If batch is not public, but exists, let it render and revalidate it on the client-side.
    if (batchDetails?.errorCode === 'E11002')
      return {
        props: { batchUID, faqs: faqsResponse?.faqs },
        revalidate: 5 // 5 seconds
      };

    const { goal } = batchDetails;
    const freeTrialDataPromise = Promise.resolve(
      fetchFreeTrialInfo(goal?.uid, batchUID)
    );
    const feedbackPromise = Promise.resolve(fetchBatchFeedbacks(goal.uid));

    const [freeTrialData, feedback] = await Promise.all([
      freeTrialDataPromise,
      feedbackPromise
    ]);

    return {
      props: {
        goalUID: goal.uid,
        batchUID: escape(batchUID),
        freeTrialData,
        fallbackData: {
          batchDetails,
          topicsCovered,
          testSeries,
          schedule,
          trialClasses,
          feedback: feedback || { results: [] },
          faqs: faqsResponse?.faqs,
          demoClasses,
          storePlaylistFeed,
          storePlaylistTestFeed
        }
      },
      revalidate: 5 // 5 seconds, as this page is cached on cdn
    };
  } catch {
    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

BatchPageWithSWR.Layout = Layout;
BatchPageWithSWR.Wrapper = PageReduxProvider;
